import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth-context";
import { Toaster } from "@/components/ui/sonner";
import { initAnalytics, trackPageview, identifyUser, resetAnalytics } from "@/lib/analytics";

import MarketingHome from "@/pages/marketing/Home";
import MarketingPricing from "@/pages/marketing/Pricing";
import MarketingFaq from "@/pages/marketing/Faq";
import MarketingTerms from "@/pages/marketing/Terms";
import MarketingPrivacy from "@/pages/marketing/Privacy";

import Login from "@/pages/auth/Login";
import Signup from "@/pages/auth/Signup";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import AuthCallback from "@/pages/auth/AuthCallback";

import Onboarding from "@/pages/app/Onboarding";
import Dashboard from "@/pages/app/Dashboard";
import Bills from "@/pages/app/Bills";
import Income from "@/pages/app/Income";
import PayPeriods from "@/pages/app/PayPeriods";
import Planning from "@/pages/app/Planning";
import YearGlance from "@/pages/app/YearGlance";
import Settings from "@/pages/app/Settings";
import Accounts from "@/pages/app/Accounts";
import Debts from "@/pages/app/Debts";
import Subscription from "@/pages/app/Subscription";
import SubscriptionSuccess from "@/pages/app/SubscriptionSuccess";
import AdminFeedback from "@/pages/app/AdminFeedback";
import AdminUsers from "@/pages/app/AdminUsers";
import AdminUserDetail from "@/pages/app/AdminUserDetail";
import AdminBillDuplicates from "@/pages/app/AdminBillDuplicates";
import PreviewIncomeGrouping from "@/pages/app/PreviewIncomeGrouping";
import AppLayout from "@/components/layout/AppLayout";
import SnapshotView from "@/pages/public/SnapshotView";

function Protected({ children, requireOnboarding = true }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#59605C]" data-testid="auth-loading">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (requireOnboarding && !user.onboarding_completed) return <Navigate to="/app/onboarding" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  const { user, loading } = useAuth();

  // Init PostHog once (no-op if REACT_APP_POSTHOG_KEY isn't set).
  useEffect(() => { initAnalytics(); }, []);

  // Track SPA route changes as pageviews so funnels/retention work.
  useEffect(() => { trackPageview(location.pathname); }, [location.pathname]);

  // Associate events with the signed-in user once auth resolves. When the
  // user signs out, `user` becomes null and we reset the distinct id.
  useEffect(() => {
    if (loading) return;
    if (user) identifyUser(user);
    else resetAnalytics();
  }, [user, loading]);

  // Emergent OAuth: session_id arrives in hash; process synchronously
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Marketing */}
      <Route path="/" element={<MarketingHome />} />
      <Route path="/pricing" element={<MarketingPricing />} />
      <Route path="/faq" element={<MarketingFaq />} />
      <Route path="/terms" element={<MarketingTerms />} />
      <Route path="/privacy" element={<MarketingPrivacy />} />

      {/* Public snapshot viewer — unauthenticated, sharable link */}
      <Route path="/s/:snapId" element={<SnapshotView />} />
      {/* Auth */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* App (protected) */}
      <Route
        path="/app/onboarding"
        element={
          <Protected requireOnboarding={false}>
            <Onboarding />
          </Protected>
        }
      />
      <Route
        path="/app"
        element={
          <Protected>
            <AppLayout />
          </Protected>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="bills" element={<Bills />} />
        <Route path="income" element={<Income />} />
        <Route path="pay-periods" element={<PayPeriods />} />
        <Route path="planning" element={<Planning />} />
        <Route path="year" element={<YearGlance />} />
        <Route path="settings" element={<Settings />} />
        <Route path="accounts" element={<Accounts />} />
        <Route path="debts" element={<Debts />} />
        <Route path="subscription" element={<Subscription />} />
        <Route path="subscription/success" element={<SubscriptionSuccess />} />
        <Route path="admin/feedback" element={<AdminFeedback />} />
        <Route path="admin/users" element={<AdminUsers />} />
        <Route path="admin/users/:id" element={<AdminUserDetail />} />
        <Route path="admin/bill-duplicates" element={<AdminBillDuplicates />} />
        {/* Design preview — not linked from nav, direct URL only. */}
        <Route path="preview/income-grouping" element={<PreviewIncomeGrouping />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
